/**
 * OrderDetailPage — Single order view matching live MRT app layout
 *
 * Layout (top to bottom):
 *   1. Breadcrumb: Orders > Order summary
 *   2. Header: Order# + status badge + cost breakdown box + action buttons
 *   3. Meta row: Ordered date | Ordered by | Invoice # | Delivery method | Criticality
 *   4. Tabs: Summary | Communication
 *   5. Ship-to banner (dark bar)
 *   6. Line items table: Image | Item details | Status | Qty | Unit price | Adjustments | Tax | Item total
 *
 * Action buttons:
 *   - Reorder (blue primary) → opens ReorderModal
 *   - Return order (gray, enabled only when delivered)
 *   - Cancel order (gray, enabled only when processing/pending)
 *
 * Contact triggers (contextual):
 *   - "Report an issue" → order-issue topic
 *   - "Return request" → return-request topic
 *
 * @module pages/OrderDetailPage
 * @since 2026-05-25 (Group G — commerce flow redesign)
 */
import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import { useContactModal } from '@contexts/ContactModalContext';
import OrderCommunications from '@components/commerce/OrderCommunications';
import ReorderModal from '@components/commerce/ReorderModal';
import { DEMO_ORDERS, LINE_STATUS_CONFIG } from '@data/demoData';

// ── Helpers ───────────────────────────────────────────────────────

const ORDER_STATUS_CONFIG = {
  processing:  { label: 'Processing',  cls: 'bg-electric-blue text-white' },
  in_transit:  { label: 'In transit',  cls: 'bg-warning text-carbon-black' },
  delivered:   { label: 'Delivered',   cls: 'bg-success text-white' },
  cancelled:   { label: 'Cancelled',   cls: 'bg-n3 text-n6' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatCurrency(amount, currency = 'CAD') {
  if (amount == null || isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount);
}

function StatusBadge({ status, config = ORDER_STATUS_CONFIG }) {
  const c = config[status] || { label: status, cls: 'bg-n2 text-n6' };
  return (
    <span className={`inline-block px-3 py-0.5 text-xs font-normal ${c.cls}`}>
      {c.label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const { openContact } = useContactModal();
  const [activeTab, setActiveTab] = useState('summary');
  const [reorderOpen, setReorderOpen] = useState(false);

  const order = useMemo(
    () => DEMO_ORDERS.find(o => o.orderId === orderId),
    [orderId]
  );

  const statusConf = ORDER_STATUS_CONFIG[order?.status] || { label: order?.status, cls: 'bg-n2 text-n6' };
  const contactContext = { orderNumber: order?.orderNumber || orderId || '' };

  // Shipping address display
  const shipAddr = order?.shippingAddress;
  const shipAddrStr = typeof shipAddr === 'string'
    ? shipAddr
    : shipAddr
      ? `${shipAddr.street}, ${shipAddr.city} ${shipAddr.province} ${shipAddr.postalCode}, ${shipAddr.country}`
      : '—';

  const canReturn = order?.status === 'delivered';
  const canCancel = order?.status === 'processing';



  return (
    <div className="sandvik-container py-8">
      <Breadcrumbs items={[
        { label: 'Home', href: '/' },
        { label: 'My orders', href: '/orders' },
        { label: 'Order summary' },
      ]} />

      {!order ? (
        <div className="border border-n2 p-8 text-center">
          <span className="material-symbols-outlined text-4xl text-n3 mb-3 block">search_off</span>
          <p className="text-n5 font-sandvik-text">Order not found.</p>
          <Link to="/orders" className="text-electric-blue hover:underline text-sm mt-2 inline-block">
            Back to orders
          </Link>
        </div>
      ) : (
        <>
          {/* ── Header area ─────────────────────────────────── */}
          <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-4">
            Order summary
          </h1>

          <div className="flex flex-wrap gap-6 mb-6">
            {/* Left: Order# + status */}
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-3 mb-3">
                <span className="material-symbols-outlined text-2xl text-electric-blue">receipt_long</span>
                <span className="text-xl font-sandvik-headline text-carbon-black">{order.orderNumber}</span>
                <StatusBadge status={order.status} />
              </div>
            </div>

            {/* Center: Cost breakdown */}
            <div className="border border-n2 px-5 py-3 min-w-[240px]">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between gap-8">
                  <span className="text-n5">Items subtotal</span>
                  <span className="text-carbon-black">{formatCurrency(order.subtotal, order.currency)}</span>
                </div>
                <div className="flex justify-between gap-8">
                  <span className="text-n5">Shipping &amp; handling</span>
                  <span className="text-carbon-black">{formatCurrency(order.shippingHandling, order.currency)}</span>
                </div>
                <div className="flex justify-between gap-8">
                  <span className="text-n5">Taxes</span>
                  <span className="text-carbon-black">{formatCurrency(order.taxes, order.currency)}</span>
                </div>
                <div className="flex justify-between gap-8 border-t border-n2 pt-1 mt-1">
                  <span className="font-normal text-carbon-black">Total</span>
                  <span className="font-normal text-carbon-black">{formatCurrency(order.totalAmount, order.currency)}</span>
                </div>
              </div>
            </div>

            {/* Right: Action buttons */}
            <div className="flex flex-col gap-2 min-w-[160px]">
              <button
                onClick={() => setReorderOpen(true)}
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-electric-blue text-white text-sm font-sandvik-text hover:bg-eb-3 transition-colors"
              >
                <span className="material-symbols-outlined text-base">replay</span>
                Reorder
              </button>
              <button
                onClick={() => canReturn && openContact('return-request', contactContext)}
                disabled={!canReturn}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 border text-sm font-sandvik-text transition-colors
                  ${canReturn
                    ? 'border-n3 text-carbon-black hover:border-electric-blue hover:text-electric-blue'
                    : 'border-n2 text-n3 cursor-not-allowed'}`}
              >
                <span className="material-symbols-outlined text-base">assignment_return</span>
                Return order
              </button>
              <button
                onClick={() => canCancel && openContact('order-issue', { ...contactContext, subject: 'Cancel order ' + order.orderNumber })}
                disabled={!canCancel}
                className={`flex items-center justify-center gap-2 px-5 py-2.5 border text-sm font-sandvik-text transition-colors
                  ${canCancel
                    ? 'border-n3 text-carbon-black hover:border-electric-blue hover:text-electric-blue'
                    : 'border-n2 text-n3 cursor-not-allowed'}`}
              >
                <span className="material-symbols-outlined text-base">cancel</span>
                Cancel order
              </button>
            </div>
          </div>

          {/* ── Meta info row ───────────────────────────────── */}
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm mb-6 border border-n2 px-5 py-3">
            <div>
              <span className="text-n5">Ordered date</span>
              <p className="text-carbon-black font-normal">{formatDate(order.orderDate)}</p>
            </div>
            <div>
              <span className="text-n5">Ordered by</span>
              <p className="text-electric-blue font-normal">{order.orderedBy || '—'}</p>
            </div>
            <div>
              <span className="text-n5">Invoice #</span>
              <p className="text-carbon-black font-normal">
                {order.invoiceNumber ? (
                  <Link to="/invoices" className="text-electric-blue hover:underline">
                    {order.invoiceNumber}
                  </Link>
                ) : '—'}
              </p>
            </div>
            <div>
              <span className="text-n5">Delivery method</span>
              <p className="text-carbon-black font-normal">{order.deliveryMethod || '—'}</p>
            </div>
            <div>
              <span className="text-n5">Order criticality</span>
              <p className="text-carbon-black font-normal">{order.orderCriticality || '—'}</p>
            </div>
            {order.customerRef && (
              <div>
                <span className="text-n5">Buyer&apos;s reference</span>
                <p className="text-carbon-black font-normal">{order.customerRef}</p>
              </div>
            )}
          </div>

          {/* ── Tabs: Summary | Communication ───────────────── */}
          <div className="border-b border-n2 mb-0">
            <nav className="flex gap-6" role="tablist">
              {[
                { key: 'summary', label: 'Summary' },
                { key: 'communication', label: 'Communication' },
              ].map(tab => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`relative pb-3 text-sm font-normal transition-colors
                    ${activeTab === tab.key
                      ? 'text-electric-blue'
                      : 'text-n5 hover:text-carbon-black'}`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-blue" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* ── Tab content ─────────────────────────────────── */}
          {activeTab === 'summary' ? (
            <>
              {/* Ship-to banner */}
              <div className="bg-carbon-black text-white px-5 py-3 flex items-center gap-2 mb-0">
                <span className="material-symbols-outlined text-lg">local_shipping</span>
                <span className="text-sm font-normal">Ship to:</span>
                <span className="text-sm">{shipAddrStr}</span>
              </div>

              {/* Line items table */}
              <div className="border border-n2 border-t-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-n2 bg-n1 text-xs text-n5">
                        <th className="text-left px-4 py-3 font-normal w-16">Image</th>
                        <th className="text-left px-4 py-3 font-normal">Item details</th>
                        <th className="text-left px-4 py-3 font-normal w-28">Status</th>
                        <th className="text-right px-4 py-3 font-normal w-16">Qty</th>
                        <th className="text-right px-4 py-3 font-normal w-24">Unit price</th>
                        <th className="text-right px-4 py-3 font-normal w-24">Adjustments</th>
                        <th className="text-right px-4 py-3 font-normal w-20">Tax</th>
                        <th className="text-right px-4 py-3 font-normal w-24">Item total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.lines?.map(line => (
                        <tr key={line.lineNumber} className="border-b border-n1 hover:bg-n1/50">
                          {/* Image placeholder */}
                          <td className="px-4 py-3">
                            <div className="w-12 h-12 bg-n1 border border-n2 flex items-center justify-center">
                              <span className="material-symbols-outlined text-n3 text-lg">
                                {line.productLine === 'dth' ? 'hardware' : 'construction'}
                              </span>
                            </div>
                          </td>
                          {/* Item details */}
                          <td className="px-4 py-3">
                            <p className="text-carbon-black font-normal text-sm">{line.description}</p>
                            <p className="text-xs text-n5 mt-0.5">
                              Part Number:{' '}
                              <Link to={`/product/${line.itemCode}`} className="text-electric-blue hover:underline">
                                {line.itemCode}
                              </Link>
                            </p>
                          </td>
                          {/* Per-line status */}
                          <td className="px-4 py-3">
                            <StatusBadge status={line.status} config={LINE_STATUS_CONFIG} />
                          </td>
                          {/* Qty */}
                          <td className="px-4 py-3 text-right text-carbon-black">{line.qty}</td>
                          {/* Unit price */}
                          <td className="px-4 py-3 text-right text-n5">
                            {formatCurrency(line.unitPrice, order.currency)}
                          </td>
                          {/* Adjustments */}
                          <td className="px-4 py-3 text-right">
                            {line.adjustments ? (
                              <span className={line.adjustments < 0 ? 'text-success' : 'text-n5'}>
                                {formatCurrency(line.adjustments, order.currency)}
                              </span>
                            ) : (
                              <span className="text-n4">{formatCurrency(0, order.currency)}</span>
                            )}
                          </td>
                          {/* Tax */}
                          <td className="px-4 py-3 text-right text-n5">
                            {formatCurrency(line.tax, order.currency)}
                          </td>
                          {/* Item total */}
                          <td className="px-4 py-3 text-right font-normal text-carbon-black">
                            {formatCurrency(line.lineTotal, order.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-n2 bg-n1">
                        <td colSpan="7" className="px-4 py-3 text-right text-sm font-normal text-carbon-black">
                          Total
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-normal text-carbon-black">
                          {formatCurrency(order.totalAmount, order.currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Tracking info */}
              {order.trackingNumber && (
                <div className="border border-n2 border-t-0 px-5 py-3 flex items-center gap-2 text-sm">
                  <span className="material-symbols-outlined text-base text-n5">local_shipping</span>
                  <span className="text-n5">{order.carrier}</span>
                  <span className="text-carbon-black font-normal">{order.trackingNumber}</span>
                </div>
              )}
            </>
          ) : (
            <div className="border border-n2 border-t-0 px-5">
              <OrderCommunications />
            </div>
          )}

          {/* ── ReorderModal (lazy) ─────────────────────────── */}
          {reorderOpen && (
            <ReorderModal
              order={order}
              onClose={() => setReorderOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
